import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { formatIQD, formatDateTime } from '../../../shared/format.js';
import { toast } from '../../../shared/useToast.js';
import { useAuth } from '../../../shared/useAuth.js';
import { useShiftStatus } from '../../../shared/useShift.js';
import {
  useDebt,
  useCustomerDebts,
  useSettleDebt,
  useReverseDebtPayment
} from '../hooks/useDebts.js';

export const METHODS = [
  { key: 'cash', label: 'نقداً', emoji: '💵' },
  { key: 'card', label: 'بطاقة', emoji: '💳' },
  { key: 'transfer', label: 'تحويل', emoji: '🏦' },
  { key: 'card_balance', label: 'رصيد الكارت', emoji: '🎫' },
  { key: 'external', label: 'خارجي', emoji: '📝' }
];

export const METHOD_LABELS = METHODS.reduce((acc, m) => ({ ...acc, [m.key]: m.label }), {});

export function StatusPill({ status, label }) {
  const variant = status === 'paid' ? 'success' : status === 'partial' ? 'warning' : 'danger';
  return <Pill variant={variant}>{label || (status === 'paid' ? 'مسدد بالكامل' : status === 'partial' ? 'مسدد جزئياً' : 'غير مسدد')}</Pill>;
}

function Modal({ title, sub, onClose, children, footer, width = 520 }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(15,16,18,0.5)' }}>
      <div className="bg-white rounded-[14px] shadow-card w-full overflow-hidden max-h-[92vh] flex flex-col" style={{ maxWidth: width }}>
        <div className="card-h">
          <div className="flex-1">
            <h3 className="text-[15px] font-bold m-0">{title}</h3>
            {sub ? <div className="text-[11.5px] text-ink-3 mt-0.5">{sub}</div> : null}
          </div>
          <button type="button" className="text-ink-3 hover:text-ink" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">{children}</div>
        {footer ? <div className="card-h border-b-0 border-t flex gap-2">{footer}</div> : null}
      </div>
    </div>
  );
}

function Row({ label, value, strong, color }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span className="text-ink-3">{label}</span>
      <span className={strong ? 'font-extrabold' : 'font-semibold'} style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// تسديد دين — كلياً أو جزئياً
export function SettleDebtModal({ debt, onClose }) {
  const invoiceId = String(debt?.invoiceId || debt?._id || '');
  const detail = useDebt(invoiceId);
  const settle = useSettleDebt();
  const { shift, blocked } = useShiftStatus();

  // المتبقي الموثوق من الخادم (قد تكون القائمة قديمة لحظة فتح النافذة).
  const remaining = Math.max(0, Number(detail.data?.debt?.remainingAmount ?? debt?.remainingAmount ?? 0));
  const original = Number(detail.data?.debt?.originalAmount ?? debt?.originalAmount ?? 0);
  const paid = Number(detail.data?.debt?.paidAmount ?? debt?.paidAmount ?? 0);
  const payments = detail.data?.payments || [];

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (remaining > 0 && amount === '') setAmount(remaining);
  }, [remaining]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = Math.max(0, Math.round(Number(amount) || 0));
  const tooMuch = value > remaining;
  const invalid = !value || tooMuch;
  const after = Math.max(0, remaining - value);

  async function submit() {
    if (tooMuch) {
      toast.error(`المبلغ أكبر من المتبقي من الدين — المتبقي ${formatIQD(remaining)}`);
      return;
    }
    const res = await settle.mutateAsync({
      invoiceId,
      amount: value,
      paymentMethod: method,
      cardUid: method === 'card_balance' ? (debt?.cardUid || undefined) : undefined,
      notes: notes || undefined
    });
    toast.success(
      res?.fullySettled
        ? `تم تسديد الدين بالكامل (${formatIQD(value)})`
        : `تم تسديد ${formatIQD(value)} — المتبقي ${formatIQD(res?.remainingAmount || 0)}`
    );
    onClose();
  }

  return (
    <Modal
      title="تسديد دين"
      sub={`فاتورة ${debt?.number || ''} — ${debt?.customer?.name || 'بدون عميل محدد'}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn flex-1 justify-center" onClick={onClose}>إلغاء</button>
          <button
            type="button"
            className="btn btn-primary flex-1 justify-center"
            onClick={submit}
            disabled={invalid || settle.isPending}
          >
            <Icon name="check" size={14} />
            {settle.isPending ? 'جاري التسديد...' : `تسديد ${formatIQD(value)}`}
          </button>
        </>
      )}
    >
      {blocked ? (
        <div className="card p-3 text-[12.5px]" style={{ background: '#FFF7ED', borderColor: '#FED7AA' }}>
          ⚠️ لا يوجد شفت مفتوح — التحصيل النقدي يتطلب فتح شفت أولاً ليدخل المال في درجك.
        </div>
      ) : null}

      <div className="card p-3 space-y-1.5" style={{ background: '#FFFCF7' }}>
        <Row label="المبلغ الأصلي" value={formatIQD(original)} />
        <Row label="المسدَّد سابقاً" value={formatIQD(paid)} color="#15803D" />
        <Row label="المتبقي من الدين" value={formatIQD(remaining)} strong color="#B91C1C" />
        <Row label="تاريخ الدين" value={formatDateTime(debt?.createdAt)} />
      </div>

      <div>
        <label className="block text-xs font-semibold text-ink-2 mb-1">المبلغ المستلم <span className="text-red">*</span></label>
        <input
          type="number"
          min="1"
          max={remaining}
          className="input"
          style={{ fontSize: 20, fontWeight: 800 }}
          value={amount}
          autoFocus
          onChange={(e) => setAmount(e.target.value === '' ? '' : Math.max(0, Math.round(Number(e.target.value) || 0)))}
          placeholder="0"
        />
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <button type="button" className="btn" onClick={() => setAmount(remaining)}>تسديد كامل المتبقي</button>
          {[0.5, 0.25].map((r) => (
            <button
              key={r}
              type="button"
              className="btn"
              onClick={() => setAmount(Math.max(1, Math.round(remaining * r)))}
            >
              {r === 0.5 ? 'نصف المتبقي' : 'ربع المتبقي'}
            </button>
          ))}
        </div>
        {tooMuch ? (
          <div className="text-[12px] text-red font-semibold mt-1.5">
            لا يُقبل مبلغ أكبر من المتبقي ({formatIQD(remaining)}).
          </div>
        ) : (
          <div className="text-[11.5px] text-ink-3 mt-1.5">
            المتبقي بعد هذه الدفعة: <b>{formatIQD(after)}</b>
            {after === 0 && value > 0 ? ' — سيصبح الدين مسدداً بالكامل' : ''}
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-ink-2 mb-1">طريقة الدفع <span className="text-red">*</span></label>
        <div className="flex gap-1.5 flex-wrap">
          {METHODS.map((m) => (
            <button
              key={m.key}
              type="button"
              className={`btn ${method === m.key ? 'btn-primary' : ''}`}
              onClick={() => setMethod(m.key)}
            >
              <span>{m.emoji}</span> {m.label}
            </button>
          ))}
        </div>
        <div className="text-[11px] text-ink-3 mt-1.5">
          {method === 'cash'
            ? `النقد يدخل درج شفتك${shift ? ` «${shift.cashboxName}»` : ''} ويظهر في تقرير الشفت والقاصة.`
            : method === 'card_balance'
              ? 'يُخصم من رصيد كارت العميل — لا يمسّ القاصة (المال دخلها وقت التعبئة).'
              : 'يُحصَّل خارج الدرج النقدي (جهاز البطاقات/البنك) فلا يُزاد رصيد القاصة.'}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات</label>
        <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="اختياري" />
      </div>

      {payments.length ? (
        <div>
          <div className="text-xs font-semibold text-ink-2 mb-1">دفعات سابقة على هذا الدين</div>
          <div className="space-y-1">
            {payments.map((p) => (
              <div key={p._id} className="flex items-center justify-between text-[12px] border-b border-line pb-1">
                <span className="text-ink-3">{formatDateTime(p.createdAt)}</span>
                <span>{METHOD_LABELS[p.paymentMethod] || p.paymentMethod}</span>
                <span className="text-ink-3">{p.userName}</span>
                <span className={p.status === 'reversed' ? 'line-through text-ink-3' : 'font-bold'}>
                  {formatIQD(p.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// ملف العميل: ديونه + سجلّ دفعاته
export function CustomerDebtsModal({ guardianId, onSettle, onClose }) {
  const q = useCustomerDebts(guardianId);
  const reverse = useReverseDebtPayment();
  const hasPermission = useAuth((s) => s.hasPermission);
  const canReverse = hasPermission('debts.settle') && hasPermission('pos.refund');
  const [tab, setTab] = useState('debts');

  const data = q.data;
  const totals = data?.totals;
  const debts = data?.debts || [];
  const payments = data?.payments || [];

  const openDebts = useMemo(() => debts.filter((d) => d.remainingAmount > 0), [debts]);

  async function doReverse(p) {
    const reason = window.prompt('سبب إلغاء هذه الدفعة؟');
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error('سبب الإلغاء مطلوب (3 أحرف على الأقل)');
      return;
    }
    await reverse.mutateAsync({ paymentId: p._id, reason: reason.trim() });
  }

  return (
    <Modal
      title={data?.customer?.name || 'ملف العميل'}
      sub={data?.customer?.phone ? `هاتف: ${data.customer.phone}` : 'سجلّ الديون والدفعات'}
      onClose={onClose}
      width={760}
      footer={<button type="button" className="btn flex-1 justify-center" onClick={onClose}>إغلاق</button>}
    >
      {q.isLoading ? (
        <div className="text-center py-8 text-ink-3">جاري التحميل...</div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'عدد الديون', value: totals?.debtsCount ?? 0, color: '#4B5563' },
              { label: 'إجمالي الأصلي', value: formatIQD(totals?.originalTotal || 0), color: '#1D4ED8' },
              { label: 'المحصَّل', value: formatIQD(totals?.paidTotal || 0), color: '#15803D' },
              { label: 'المتبقي', value: formatIQD(totals?.remainingTotal || 0), color: '#B91C1C' }
            ].map((k) => (
              <div key={k.label} className="card p-2.5">
                <div className="text-[11px] text-ink-3">{k.label}</div>
                <div className="text-[15px] font-extrabold mt-0.5" style={{ color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px]">
            {[
              { key: 'debts', label: `الديون (${debts.length})` },
              { key: 'payments', label: `سجل الدفعات (${payments.length})` }
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold ${tab === t.key ? 'bg-orange text-white' : 'text-ink-2 hover:bg-white'}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'debts' ? (
            !debts.length ? (
              <div className="text-center py-8 text-ink-3">لا ديون على هذا العميل</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="text-ink-3 border-b border-line text-right">
                      <th className="p-2">الفاتورة</th>
                      <th className="p-2">التاريخ</th>
                      <th className="p-2">الأصلي</th>
                      <th className="p-2">المسدَّد</th>
                      <th className="p-2">المتبقي</th>
                      <th className="p-2">الحالة</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {debts.map((d) => (
                      <tr key={String(d._id)} className="border-b border-line">
                        <td className="p-2 font-mono">{d.number}</td>
                        <td className="p-2 text-ink-3">{formatDateTime(d.createdAt)}</td>
                        <td className="p-2">{formatIQD(d.originalAmount)}</td>
                        <td className="p-2 text-green font-semibold">{formatIQD(d.paidAmount)}</td>
                        <td className="p-2 font-bold" style={{ color: d.remainingAmount ? '#B91C1C' : '#15803D' }}>
                          {formatIQD(d.remainingAmount)}
                        </td>
                        <td className="p-2"><StatusPill status={d.status} label={d.statusLabel} /></td>
                        <td className="p-2">
                          {d.remainingAmount > 0 && onSettle ? (
                            <button type="button" className="btn btn-primary" onClick={() => onSettle(d)}>تسديد</button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {openDebts.length ? (
                  <div className="text-[11.5px] text-ink-3 mt-2">
                    {openDebts.length} دين ما زال مستحقاً بمجموع {formatIQD(totals?.remainingTotal || 0)}.
                  </div>
                ) : null}
              </div>
            )
          ) : !payments.length ? (
            <div className="text-center py-8 text-ink-3">لا دفعات مسجّلة</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-ink-3 border-b border-line text-right">
                    <th className="p-2">التاريخ</th>
                    <th className="p-2">الفاتورة</th>
                    <th className="p-2">المبلغ</th>
                    <th className="p-2">الطريقة</th>
                    <th className="p-2">المستخدم</th>
                    <th className="p-2">المتبقي بعدها</th>
                    <th className="p-2">ملاحظات</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={String(p._id)} className="border-b border-line">
                      <td className="p-2 text-ink-3">{formatDateTime(p.createdAt)}</td>
                      <td className="p-2 font-mono">{p.invoiceNumber}</td>
                      <td className={`p-2 font-bold ${p.status === 'reversed' ? 'line-through text-ink-3' : ''}`}>
                        {formatIQD(p.amount)}
                      </td>
                      <td className="p-2">{METHOD_LABELS[p.paymentMethod] || p.paymentMethod}</td>
                      <td className="p-2">{p.userName || '—'}</td>
                      <td className="p-2">{formatIQD(p.remainingAfter)}</td>
                      <td className="p-2 text-ink-3">
                        {p.status === 'reversed' ? <Pill variant="danger">ملغاة — {p.reverseReason}</Pill> : (p.notes || '—')}
                      </td>
                      <td className="p-2">
                        {p.status === 'posted' && canReverse ? (
                          <button
                            type="button"
                            className="btn btn-danger"
                            disabled={reverse.isPending}
                            onClick={() => doReverse(p)}
                          >
                            إلغاء
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
